// DashboardLayout.jsx — Clean & Minimalist Identity
import React from 'react';
import { UserCircle, LogOut, Menu, X, Home, Facebook, Youtube, MessageCircle, Sun, Moon } from 'lucide-react';
import { Footer } from './Footer';
import { FloatingWhatsApp } from './FloatingWhatsApp';

export const DashboardLayout = ({
  menuItems,
  activeTab,
  setActiveTab,
  isDarkMode,
  setIsDarkMode,
  signOut,
  profile,
  sidebarOpen,
  setSidebarOpen,
  bottomSlot,
  children,
}) => {
  const displayName =
    profile?.fullName ||
    [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' ') ||
    'مستخدم';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-[#0f0a1e] text-gray-100' : 'bg-[#FAFAFA] text-gray-900'}`} dir="rtl">

      {/* ══════════════ TOP NAVBAR ══════════════ */}
      <header className="fixed top-0 right-0 left-0 h-16 z-50 flex items-center justify-between px-6"
        style={{ background: 'rgba(15,15,26,0.85)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderBottom: '2px solid rgba(201,168,76,0.15)', boxShadow: '0 4px 30px rgba(0,0,0,0.3)' }}>

        {/* Right: Brand */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: '#C9A84C' }}>
            <img
              src="/images/logo.png"
              alt="منصة أ. محمد العاطفي"
              className="w-7 h-7 rounded-lg object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span style="font-weight:900;color:#1A1A2E;font-size:1rem">أ</span>';
              }}
            />
          </div>
          <h1 className="text-base font-black tracking-tight leading-tight">
            <span className="text-white">منصة أ.</span>
            <span style={{ color: '#C9A84C' }}> محمد العاطفي</span>
          </h1>
        </div>

        {/* Left: avatar + logout + hamburger */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            <UserCircle size={18} style={{ color: '#C9A84C' }} />
            <span className="text-sm font-bold text-white">{displayName}</span>
          </div>

          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-2 rounded-xl transition hover:bg-white/10 text-[#C9A84C]"
            title={isDarkMode ? 'تفعيل الوضع النهاري' : 'تفعيل الوضع الليلي'}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <button
            onClick={signOut}
            className="p-2 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition"
            title="تسجيل الخروج"
          >
            <LogOut size={18} />
          </button>

          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-xl hover:bg-white/10 transition text-white"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* ══════════════ SIDEBAR ══════════════ */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`fixed top-16 right-0 h-[calc(100vh-4rem)] w-64 flex flex-col z-40 transition-all duration-300 overflow-y-auto
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}
        style={{ background: 'rgba(15,10,30,0.88)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderLeft: '1px solid rgba(201,168,76,0.12)' }}
      >
        {/* User card */}
        <div className="p-5 text-center" style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}>
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-3 shadow-lg"
              style={{ background: 'rgba(201,168,76,0.1)', border: '2px solid rgba(201,168,76,0.3)' }}>
              <UserCircle size={52} style={{ color: '#C9A84C' }} />
            </div>
            <h3 className="font-black text-sm leading-tight text-white mb-1">{displayName}</h3>
            {profile?.grade && (
              <p className="text-xs text-white/50 mt-0.5">
                {profile.grade === '1sec' ? 'الصف الأول الثانوي' :
                 profile.grade === '2sec' ? 'الصف الثاني الثانوي' :
                 profile.grade === '3sec' ? 'الصف الثالث الثانوي' : profile.grade}
              </p>
            )}
            {(profile?.role === 'admin' || profile?.role === 'boss') && (
              <span className="mt-2 inline-block px-3 py-1 rounded-full text-xs font-black"
                style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)' }}>
                {profile.role === 'boss' ? '👑 المدير الأعلى' : '🛡️ مدير'}
              </span>
            )}
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-3 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.Icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 font-bold text-sm ${
                  isActive
                    ? 'text-[#1A1A2E] shadow-lg'
                    : 'text-white/70 hover:text-[#C9A84C] hover:bg-white/5'
                }`}
                style={isActive ? { background: 'linear-gradient(135deg, #C9A84C, #A68A3E)', boxShadow: '0 4px 16px rgba(201,168,76,0.3)' } : {}}
              >
                {Icon && (
                  <Icon size={18} style={{ color: isActive ? '#1A1A2E' : undefined }} className={!isActive ? 'text-[#C9A84C]/60' : ''} />
                )}
                <span className="flex-1 text-right">{item.label}</span>
                {item.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-black ${
                    isActive ? 'bg-[#1A1A2E]/20 text-[#1A1A2E]' : 'text-white/50'
                  }`}
                  style={!isActive ? { background: 'rgba(201,168,76,0.12)', color: '#C9A84C' } : {}}>
                    {item.count}
                  </span>
                )}
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse font-black">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bottom slot */}
        {bottomSlot && (
          <div className="p-4 space-y-2" style={{ borderTop: '1px solid rgba(201,168,76,0.12)' }}>
            {bottomSlot}
          </div>
        )}
      </aside>

      {/* ══════════════ MAIN CONTENT ══════════════ */}
      <main className="lg:mr-64 mt-16 min-h-[calc(100vh-4rem)]" style={{ color: isDarkMode ? '#e2e8f0' : '#1a1a1a' }}>
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>

        {/* Global Footer injected onto Dashboard */}
        <div className="mt-8 relative z-10 w-full" style={{ position: 'relative', overflow: 'hidden' }}>
             <Footer />
        </div>

        {/* Floating WhatsApp Bubble */}
        <FloatingWhatsApp />
      </main>
    </div>
  );
};